import { Component, Inject, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
import DsModal from '@/shared/modal/ds-modal.vue';
import ScrapAgentService from '@/service/scrap-agent.service';
import { Account } from '@/shared/model/scrap-agent.model';

interface AppInfo {
  redirectUrl: string;
  clientId: string;
  scope: string;
  appId: number;
  clientSecret: string;
}

@Component({
  components: { DsModal },
})
export default class AccountConnectLoginCloudComponent extends Vue {
  @Prop({ type: Boolean, required: true }) open!: boolean;
  @PropSync('channelConnect', { type: Object }) syncedChannelConnect!: any;

  @Inject('scrapAgentService')
  public scrapAgentService!: () => ScrapAgentService;

  showModal = this.open;
  appInfo: AppInfo | null = null;
  baseUrl = window.location.origin;
  authState: string = this.generateWindowId();
  messageProcessed = false;

  formData = {
    accountId: String(Math.floor(100000 + Math.random() * 900000)) + 'Youtube',
    accountName: '',
    password: '',
    channelId: null,
    channelTitle: null,
    channelDescription: null,
    channelThumbnail: null,
    subscriberCount: null,
    videoCount: null,
  } as any;

  @Watch('open')
  onOpenChange(value: boolean) {
    this.showModal = value;
    window.removeEventListener('message', this.receiveMessage);
    window.addEventListener('message', this.receiveMessage, false);
    if (value) {
      this.scrapAgentService()
        .getAppInfo(this.syncedChannelConnect.channelType)
        .then((result: any) => {
          if (result && result.body && result.body.length > 0) {
            this.appInfo = {
              appId: result.body[0].appId,
              redirectUrl: result.body[0].redirectUrl,
              clientId: result.body[0].clientId,
              scope: result.body[0].scope,
              clientSecret: result.body[0].clientSecret,
            };
          }
        })
        .catch(error => {
          console.error('Failed to fetch app info:', error);
        });
    }
  }
  getCloudImageSrc(channelType: string): string {
    const cloudMap: Record<string, string> = {
      GOOGLE_DRIVE: 'https://dzh91b51qfr7r.cloudfront.net/social-channel-thumb/ggdrive.png',
      ONE_DRIVE: 'https://dzh91b51qfr7r.cloudfront.net/social-channel-thumb/onedrive.png',
      DROP_BOX: 'https://dzh91b51qfr7r.cloudfront.net/social-channel-thumb/dropbox.png',
    };
    return cloudMap[channelType] || '';
  }

  // Generate a unique id for this window instance.
  generateWindowId(): string {
    return 'id_' + Math.random().toString(36).substr(2, 9);
  }

  // Build authUrl by building the URL based on the channel type.
  get authUrl(): string {
    if (!this.appInfo) {
      return '';
    }
    // Use the pathname from appInfo.redirectUrl and the current domain.
    const redirectPath = new URL(this.appInfo.redirectUrl).pathname; // e.g. "/api/public/goAuth"
    const finalRedirectUri = encodeURIComponent(`${this.baseUrl}${redirectPath}`);
    const state = this.authState;

    // Check the channel type and build the URL accordingly.
    switch (this.syncedChannelConnect.channelType) {
      case 'GOOGLE_DRIVE': // <-- For Google Drive
        return `https://accounts.google.com/o/oauth2/v2/auth?scope=${this.appInfo.scope}&access_type=offline&prompt=consent&response_type=code&redirect_uri=${finalRedirectUri}&client_id=${this.appInfo.clientId}&state=${state}`;
      case 'ONE_DRIVE': // <-- For OneDrive (Microsoft)
        return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?response_type=code&prompt=select_account&client_id=${
          this.appInfo.clientId
        }&redirect_uri=${finalRedirectUri}&response_mode=query&scope=${encodeURIComponent(
          'https://graph.microsoft.com/Files.Read.All offline_access'
        )}&state=${state}`;
      case 'DROP_BOX': // <-- For Dropbox
        console.log(
          `Dropbox auth URL:`,
          `https://www.dropbox.com/oauth2/authorize?client_id=${
            this.appInfo.clientId
          }&response_type=code&prompt=consent&token_access_type=offline&force_reauthentication=true&redirect_uri=${finalRedirectUri}&scope=${encodeURIComponent(
            this.appInfo.scope
          )}&state=${state}`
        );
        return `https://www.dropbox.com/oauth2/authorize?client_id=${
          this.appInfo.clientId
        }&response_type=code&token_access_type=offline&force_reauthentication=true&redirect_uri=${finalRedirectUri}&scope=${encodeURIComponent(
          this.appInfo.scope
        )}&state=${state}`;
      default:
        return '';
    }
  }

  openGooglePopup() {
    if (!this.appInfo) {
      console.error('App info is not loaded yet.');
      return;
    }
    console.info(`Opening faceBook auth popup... ${this.authUrl}`);
    const width = 500,
      height = 500;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;
    const popup = window.open(this.authUrl, 'FaceBookAuth', `width=${width},height=${height},top=${top},left=${left}`);
  }

  receiveMessage(event: MessageEvent) {
    if (this.messageProcessed) {
      return;
    }
    // Verify that the message payload contains the expected state and authorization_code.
    if (event.data && event.data.authorization_code && event.data.state === this.authState) {
      this.messageProcessed = true;
      const authCode = event.data.authorization_code;
      console.log('Received matching authorization code:', authCode);
      this.handleAccountConnect(authCode);
    } else {
      this.messageProcessed = false;
      console.warn('Ignoring unexpected message payload:', event.data);
    }
  }

  async handleAccountConnect(authorizationCode: string) {
    const payload = {
      authorizationCode: authorizationCode,
      appId: this.appInfo?.appId,
      avatar: this.getCloudImageSrc(this.syncedChannelConnect.channelType),
      chanelName: this.syncedChannelConnect.channelType,
      clientId: this.appInfo.clientId,
      clientSecret: this.appInfo.clientSecret,
      redirectUrl: window.location.origin + '/api/public/goAuth',
      scope: this.appInfo.scope,
      userKey: JSON.parse(localStorage.getItem('localStorageUser') || '{}')?.id || null,
    };
    this.messageProcessed = false;
    try {
      const result = await this.scrapAgentService().addUserAccount(payload);
      const responseBody = result?.data?.body?.body?.body;
      // if (!responseBody?.isSuccess) {
      //   alert('Authorization failed. Please try again.');
      //   return;
      // }
      if (!this.syncedChannelConnect.accounts) {
        this.syncedChannelConnect.accounts = [];
      }
      if (result) {
        this.$bvToast.toast(`${this.$t('scrap-agent.toast.add-account.messAuSuccess')}`, {
          toaster: 'b-toaster-bottom-right',
          title: `${this.$t('scrap-agent.toast.add-account.titleAuSuccess')}`,
          variant: 'success',
          solid: true,
          autoHideDelay: 5000,
        });
      }
      this.$emit('modal:close', true, responseBody.accountId);
    } catch (error) {
      console.error('error: ', error);
      this.$bvToast.toast(`${this.$t('scrap-agent.toast.add-account.messFail')}`, {
        toaster: 'b-toaster-bottom-right',
        title: `${this.$t('scrap-agent.toast.add-account.titleAuFail')}`,
        variant: 'danger',
        solid: true,
        autoHideDelay: 5000,
      });
    }
  }
}
